import {
    createContext,
    useState,
    type ReactNode,
    useCallback,
    type FC,
    useContext,
    type Dispatch,
    type SetStateAction,
} from 'react';
import { useDispatch } from 'react-redux';
import { updateRelayVariables } from '../../actions/sharedRelayVariableActions.js';

type Props = {
    children: ReactNode;
};

type StateSetter<T> = Dispatch<SetStateAction<T>>;

type AuctionContext = {
    isBiddingDisabled: boolean;
    setIsBiddingDisabled: StateSetter<boolean>;
    shouldDisplayCountdown: boolean;
    setShouldDisplayCountdown: StateSetter<boolean>;
    dispatchUpdateAuctionItem: () => void;
    isCloseToReserve: boolean;
    setIsCloseToReserve: StateSetter<boolean>;
};

const PdpSharedAuctionContext = createContext<AuctionContext>({
    isBiddingDisabled: false,
    setIsBiddingDisabled: () => {},
    shouldDisplayCountdown: false,
    setShouldDisplayCountdown: () => {},
    dispatchUpdateAuctionItem: () => {},
    isCloseToReserve: false,
    setIsCloseToReserve: () => {},
});

export const useSharedAuctionContext = (): AuctionContext => useContext(PdpSharedAuctionContext);

export const PdpSharedAuctionContextProvider: FC<Props> = ({ children }) => {
    const [isBiddingDisabled, setIsBiddingDisabled] = useState(false);
    const [shouldDisplayCountdown, setShouldDisplayCountdown] = useState(false);
    const [isCloseToReserve, setIsCloseToReserve] = useState(false);
    const dispatch = useDispatch();

    // this refreshes the page to enter ACTIVE or PREVIEW state
    const dispatchUpdateAuctionItem = useCallback(() => {
        dispatch(updateRelayVariables({ updateAuctionItem: true }));
    }, [dispatch]);

    return (
        <PdpSharedAuctionContext
            value={{
                isBiddingDisabled,
                setIsBiddingDisabled,
                dispatchUpdateAuctionItem,
                shouldDisplayCountdown,
                setShouldDisplayCountdown,
                isCloseToReserve,
                setIsCloseToReserve,
            }}
        >
            {children}
        </PdpSharedAuctionContext>
    );
};
